import React, { useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { CompanyProfileForm } from '../../components/settings/CompanyProfileForm';
import { ChangePasswordForm } from '../../components/settings/ChangePasswordForm';
import { CompanyArtisanIntegration } from './CompanyArtisanIntegration';
import { MCPSettings } from './MCPSettings';
import { ActivityLogsPreview } from './ActivityLogsPreview';
import { NotificationSettingsSection } from './NotificationSettingsSection';
import { useTheme } from '../../features/theme/useTheme';
import type { ThemeMode } from '../../features/theme/preferences/types';
import { usePermissions } from '../../hooks/usePermissions';
import { useLanguage } from '../../features/language/useLanguage';
import { notificationService } from '../../services/notificationService';
import { useToast } from '../../components/common/Toast';

export const SettingsPage: React.FC = () => {
  const { themeMode, effectiveTheme, isSyncing, setThemeMode } = useTheme();
  const { t } = useLanguage();
  const { can } = usePermissions();
  const canReadCompany = can('company', 'read');
  const canWriteCompany = can('company', 'write');
  const canReadArtisan = can('settings.artisan_keys', 'read');
  const canWriteArtisan = can('settings.artisan_keys', 'write');
  const canReadMCP = can('settings.mcp_keys', 'read');
  const canWriteMCP = can('settings.mcp_keys', 'write');
  const canReadActivityLogs = can('activity_logs', 'read');
  const canManageNotifications = can('notification_settings', 'manage');
  const canReceiveNotifications = can('notifications', 'receive');

  const user = useAuthStore((state) => state.user);
  const company = useAuthStore((state) => state.company);
  const permissionsList = useAuthStore((state) => state.permissions);
  const syncProfile = useAuthStore((state) => state.syncProfile);
  const { showToast } = useToast();

  const [emailEnabled, setEmailEnabled] = useState<boolean>(
    user?.emailNotificationsEnabled ?? false
  );
  const [emailToggling, setEmailToggling] = useState(false);

  const handleEmailToggle = async () => {
    if (!user || !company || emailToggling) return;
    const next = !emailEnabled;
    setEmailEnabled(next);
    setEmailToggling(true);
    try {
      await notificationService.updateMyNotificationPreferences({
        email_notifications_enabled: next,
      });
      syncProfile(
        { ...user, emailNotificationsEnabled: next },
        company,
        permissionsList,
      );
      showToast(
        next
          ? t('settings.emailNotifications.enabledToast')
          : t('settings.emailNotifications.disabledToast'),
        'success',
      );
    } catch {
      setEmailEnabled(!next);
      showToast(t('settings.emailNotifications.errorToast'), 'error');
    } finally {
      setEmailToggling(false);
    }
  };

  const themeOptions: Array<{ value: ThemeMode; label: string; description: string }> = [
    {
      value: 'light',
      label: t('settings.themeLight'),
      description: t('settings.themeLightDescription'),
    },
    {
      value: 'dark',
      label: t('settings.themeDark'),
      description: t('settings.themeDarkDescription'),
    },
    {
      value: 'auto',
      label: t('settings.themeAuto'),
      description: t('settings.themeAutoDescription'),
    },
  ];

  return (
    <div className="space-y-6" data-test-id="settings-page-container">
      <h1 className="text-2xl font-semibold text-gray-900">{t('settings.title')}</h1>
      
      {/* Company Profile Section */}
      {canReadCompany && <CompanyProfileForm readOnly={!canWriteCompany} />}
      {/* Notification Reminder Settings — admin & super_admin only */}
      {canManageNotifications && <NotificationSettingsSection />}
      <ChangePasswordForm />

      {/* General/Personal Settings Section */}
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t('settings.personalPreferences')}
          </h3>
          <div className="mt-2 max-w-xl text-sm text-gray-500">
            <p>{t('settings.personalPreferencesDescription')}</p>
          </div>
          
          <div className="mt-5 space-y-4">
            {canReceiveNotifications && (
              <div
                className="w-full sm:w-[30rem]"
                data-test-id="settings-email-notifications-group"
              >
                <label className="block text-sm font-medium text-gray-700">
                  {t('settings.emailNotifications.label')}
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  {t('settings.emailNotifications.description')}
                </p>
                <div className="mt-2 flex items-center gap-3">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={emailEnabled}
                    aria-label={t('settings.emailNotifications.label')}
                    onClick={handleEmailToggle}
                    disabled={emailToggling}
                    data-test-id="settings-email-notifications-switch"
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      emailEnabled ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        emailEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <span className="text-sm text-gray-600">
                    {emailEnabled
                      ? t('common.on', undefined, 'On')
                      : t('common.off', undefined, 'Off')}
                  </span>
                </div>
              </div>
            )}

            <div className="w-full sm:w-[30rem]" data-test-id="settings-theme-mode-group">
              <label className="block text-sm font-medium text-gray-700">{t('settings.theme')}</label>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                {themeOptions.map((option) => {
                  const isActive = themeMode === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      data-test-id={`settings-theme-option-${option.value}`}
                      onClick={() => setThemeMode(option.value)}
                      className={`rounded-md border p-3 text-left transition-colors ${
                        isActive
                          ? 'border-primary-500 bg-primary-50 text-primary-900'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-primary-300'
                      }`}
                      aria-pressed={isActive}
                    >
                      <div className="text-sm font-medium">{option.label}</div>
                      <div className="mt-1 text-xs text-gray-500">{option.description}</div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 text-xs text-gray-500" data-test-id="settings-theme-current-status">
                {t('settings.themeCurrentStatus', {
                  mode: themeMode,
                  appearance: effectiveTheme,
                  syncing: isSyncing ? t('settings.themeSyncingSuffix') : '',
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {canReadArtisan && <CompanyArtisanIntegration readOnly={!canWriteArtisan} />}

      {/* MCP Integration Settings */}
      {canReadMCP && <MCPSettings readOnly={!canWriteMCP} />}

      {/* Activity Logs Preview — top 5, with link to full view */}
      {canReadActivityLogs && <ActivityLogsPreview />}
    </div>
  );
};
